import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { Check, Star, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { pickBg } from "@/assets/generated";
import { WaitlistDialog } from "./waitlist-dialog";
import { openWhatsAppContact } from "@/lib/whatsapp-contact";
import { MobilePricing } from "./mobile/mobile-pricing";
import { AuroraOrb } from "@/components/vision/aurora-orb";
import { Reveal, RevealStagger } from "@/components/vision/reveal";
import { EditorialHeading } from "./editorial-heading";

export function PricingSection() {
  const { t } = useTranslation();
  const [annual, setAnnual] = useState(true);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | undefined>(undefined);

  const plans = [
    {
      id: "starter",
      monthly: 79,
      annual: 63,
      name: t("pricing.plans.starter.name"),
      desc: t("pricing.plans.starter.desc"),
      features: t("pricing.plans.starter.features", { returnObjects: true }) as string[],
    },
    {
      id: "pro",
      monthly: 199,
      annual: 159,
      highlight: true,
      name: t("pricing.plans.pro.name"),
      desc: t("pricing.plans.pro.desc"),
      features: t("pricing.plans.pro.features", { returnObjects: true }) as string[],
    },
    {
      id: "studio",
      monthly: null as number | null,
      annual: null as number | null,
      name: t("pricing.plans.studio.name"),
      desc: t("pricing.plans.studio.desc"),
      features: t("pricing.plans.studio.features", { returnObjects: true }) as string[],
    },
  ];

  return (
    <>
    <div className="md:hidden"><MobilePricing /></div>
    <section id="pricing" className="relative overflow-hidden bg-[hsl(var(--surface-raised))] py-24 sm:py-32 hidden md:block">
      <img src={pickBg(28)} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-10 dark:opacity-20" loading="lazy" />
      <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--surface-raised))] via-[hsl(var(--surface-raised))]/80 to-[hsl(var(--surface-raised))]" />
      <AuroraOrb className="left-1/2 top-0 -translate-x-1/2" color="blue" size={520} />
      <AuroraOrb className="right-[-6%] bottom-[10%]" color="violet" size={400} delay={5} />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <EditorialHeading index="No. 09" eyebrow={t("pricing.eyebrow")} title={t("pricing.title")} />
        <Reveal className="mx-auto max-w-2xl text-center" y={28} blur={10}>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-1.5 text-xs font-medium text-amber-700 dark:text-amber-300">
            <Clock className="h-3.5 w-3.5" /> {t("pricing.notice")}
          </div>
          <div className="mt-8 inline-flex items-center gap-3 rounded-full border border-border bg-card p-1.5">
            <button
              onClick={() => setAnnual(false)}
              className={cn("rounded-full px-4 py-1.5 text-sm font-medium transition-colors", !annual && "bg-primary text-primary-foreground")}
            >
              {t("pricing.monthly")}
            </button>
            <button
              onClick={() => setAnnual(true)}
              className={cn("rounded-full px-4 py-1.5 text-sm font-medium transition-colors flex items-center gap-2", annual && "bg-primary text-primary-foreground")}
            >
              {t("pricing.annual")} <span className="rounded-full bg-success px-2 py-0.5 text-[10px] font-bold text-success-foreground">−20%</span>
            </button>
          </div>
        </Reveal>

        <RevealStagger className="mt-12 grid gap-6 lg:grid-cols-3" stagger={0.12} y={36} blur={10}>
          {plans.map((p) => (
            <div
              key={p.id}
              className={cn(
                "vision-card relative flex flex-col rounded-3xl p-8 transition-all duration-300 hover:-translate-y-1",
                p.highlight ? "ring-2 ring-primary/40 lg:-translate-y-2 shadow-[var(--shadow-vision)]" : ""
              )}
            >
              {p.highlight && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 rounded-full gradient-gold px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-accent-foreground shadow-md">
                  <Star className="inline h-3 w-3 mr-1" /> {t("pricing.mostChosen")}
                </div>
              )}
              <h3 className="font-display text-2xl font-bold">{p.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{p.desc}</p>
              <div className="mt-6 flex items-baseline gap-1">
                {p.monthly === null ? (
                  <span className="font-display text-4xl font-bold">{t("pricing.onRequest")}</span>
                ) : (
                  <>
                    <span className="font-display text-5xl font-bold">€{annual ? p.annual : p.monthly}</span>
                    <span className="text-sm text-muted-foreground">{t("pricing.perMonth")}</span>
                  </>
                )}
              </div>
              {annual && p.monthly !== null && (
                <p className="mt-1 text-xs text-success">{t("pricing.save", { amount: (p.monthly! - (p.annual as number)) * 12 })}</p>
              )}
              <ul className="mt-6 space-y-3 text-sm">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="h-4 w-4 shrink-0 text-success mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                size="lg"
                className={cn(
                  "mt-8",
                  p.highlight ? "bg-primary text-primary-foreground hover:bg-primary/90" : "bg-secondary text-secondary-foreground hover:bg-secondary/80"
                )}
                onClick={() => { setSelectedPlan(p.id); openWhatsAppContact(`Ciao Gianluca, sono interessato al piano ${p.name} (${annual ? "annuale" : "mensile"}) di LexAI Pro.`); }}
              >
                {t("pricing.notify")}
              </Button>
              <Button asChild variant="ghost" size="sm" className="mt-2 text-xs text-muted-foreground">
                <Link to="/signup">{t("pricing.orFree")}</Link>
              </Button>
            </div>
          ))}
        </RevealStagger>

        <p className="mt-10 text-center text-sm text-muted-foreground">
          {t("pricing.footer")}
        </p>
      </div>
      <WaitlistDialog open={waitlistOpen} onOpenChange={setWaitlistOpen} plan={selectedPlan} source="pricing" />
    </section>
    </>
  );
}
